import { Close, GroupOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import RswEditor, { EditorProvider } from "react-simple-wysiwyg";
import EmailLimitSlider from "../../components/EmailLimitSlider";
import LoadingButton from "../../components/LoadingButton";
import SearchableInboxSelect, {
  Inbox,
  loadAllInboxes,
} from "../../components/SearchableInboxSelect";
import SearchableIntegrationSelect,
{
  Integration,
} from "../../components/SearchableIntegrationSelect";
import SelectEmployeeDialog from "../../components/SelectEmployee";
import VoiceSelector from "../../components/VoiceSelector";
import { Company, Employee } from "../../types";
import { hostname } from "../../utils";
import { useEmployeeTerminology } from "../../utils/terminology";

export default ({
  open,
  onClose,
  profile,
  companyId,
  eventListener,
  getToken,
  employees,
  company,
  integrations,
}: {
  open: boolean;
  onClose: () => void;
  profile: any;
  companyId: string;
  eventListener: Function;
  getToken: Function;
  employees: Employee[];
  company: Company;
  integrations: any[];
}) => {
  const { employee: employeeText, employeeLower } = useEmployeeTerminology();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<any>({
    name: "",
    email: "",
    signature: "",
    inboxId: "",
    integrationId: "",
    linkedinIntegrationId: "",
    phoneIntegrationId: "",
    phone: "",
    voiceId: "",
    agentId: "",
    dailySendingLimit: 15,
    trackingDomainId: "",
  });
  const [employeeSelectOpen, setEmployeeSelectOpen] = useState(false);
  const [inboxes, setInboxes] = useState<Inbox[]>([]);
  const [inboxesLoading, setInboxesLoading] = useState(false);
  const [linkedinIntegrations, setLinkedinIntegrations] = useState<any[]>([]);
  const [smsIntegrations, setSmsIntegrations] = useState<any[]>([]);
  const [availablePhoneNumbers, setAvailablePhoneNumbers] = useState<any[]>([]);
  const [agents, setAgents] = useState<any[]>([]);
  const [trackingDomains, setTrackingDomains] = useState<any[]>([]);
  const [selectedInbox, setSelectedInbox] = useState<any>(null);

  const handleClose = () => {
    onClose();
  };

  const getInboxes = async () => {
    setInboxesLoading(true);
    try {
      const currentToken = await getToken();
      const allInboxes = await loadAllInboxes(currentToken, companyId);
      setInboxes(allInboxes);
    } catch (err) {
      console.error("Failed to load inboxes:", err);
    } finally {
      setInboxesLoading(false);
    }
  };

  const getLinkedInIntegrations = async () => {
    const currentToken = await getToken();
    axios
      .get(`${hostname}/integrations/by-type/browser_session`, {
        params: { companyId, type: "linkedin_login" },
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then((res) => {
        setLinkedinIntegrations(res.data.items || []);
      })
      .catch((err) => {
        setLinkedinIntegrations([]);
      });
  };

  const getSmsIntegrations = async () => {
    const currentToken = await getToken();
    axios
      .get(`${hostname}/integrations/by-type/sms`, {
        params: { companyId },
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then((res) => {
        const twilioIntegrations = res.data.items || [];
        setSmsIntegrations(twilioIntegrations);
        // Fetch phone numbers for all integrations
        fetchPhoneNumbers(twilioIntegrations);
      })
      .catch((err) => {
        setSmsIntegrations([]);
      });
  };

  const getTrackingDomains = async () => {
    const currentToken = await getToken();
    axios
      .get(`${hostname}/tracking-domains/verified`, {
        params: { companyId },
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then((res) => {
        setTrackingDomains(res.data.domains || []);
      })
      .catch((err) => {
        setTrackingDomains([]);
      });
  };

  const getAgents = async () => {
    const currentToken = await getToken();
    axios
      .get(`${hostname}/agents`, {
        params: { companyId, pageSize: 100 },
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then((res) => {
        setAgents(res.data.items || []);
      })
      .catch((err) => {
        setAgents([]);
      });
  };

  const fetchPhoneNumbers = async (integrations: any[]) => {
    const currentToken = await getToken();
    const allPhoneNumbers: any[] = [];

    for (const integration of integrations) {
      try {
        const response = await axios.get(
          `${hostname}/integrations/twilio/${integration.id}/phone-numbers`,
          {
            params: { companyId },
            headers: { Authorization: `Bearer ${currentToken}` },
          }
        );

        const phoneNumbers = response.data.phoneNumbers || [];
        phoneNumbers.forEach((number: any) => {
          allPhoneNumbers.push({
            ...number,
            integrationId: integration.id,
            integrationName:
              integration.instanceName || integration.name || "Twilio Account",
          });
        });
      } catch (error) {
        console.error(
          `Failed to fetch phone numbers for integration ${integration.id}:`,
          error
        );
      }
    }

    setAvailablePhoneNumbers(allPhoneNumbers);
  };

  const onSubmit = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    const currentToken = await getToken();

    const isEditing = profile && formData.id;
    const url = isEditing
      ? `${hostname}/sequences/profiles/${formData.id}`
      : `${hostname}/sequences/profiles`;
    const method = isEditing ? "put" : "post";

    const submitData = { ...formData, companyId };

    axios[method](url, submitData, {
      headers: { Authorization: `Bearer ${currentToken}` },
    })
      .then(() => {
        setFormData({});
        handleClose();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onChange = (e: any) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const onInboxChange = (inboxId: string, inbox: Inbox | null) => {
    setSelectedInbox(inbox);

    // Update form data with inbox ID
    const updatedFormData = { ...formData, inboxId };

    // Auto-populate email from the inbox
    // For Gmail/Office365, the domain field contains the full email address
    // For IMAP, the domain field also contains the full email address
    if (inbox && inbox.domain && inbox.domain.includes("@")) {
      updatedFormData.email = inbox.domain;
      // Also update the name if it's not already set
      if (!updatedFormData.name) {
        updatedFormData.name = inbox.domain.split("@")[0];
      }
    }

    // Auto-set the email integration from the inbox's integration
    if (inbox && inbox.integrationId) {
      updatedFormData.integrationId = inbox.integrationId;
    }

    setFormData(updatedFormData);
  };

  const onEmployeeSelect = (employee: Employee) => {
    setEmployeeSelectOpen(false);

    var companyWebsiteTag = "";
    if (company.website) {
      companyWebsiteTag = `<a href="${company.website}" style="text-decoration: none;">${company.website}</a><br>`;
    }
    const signature = `<img width="50" height="50" src="${employee.picture}" alt="${employee.name}"/><br>
<strong>${employee.name}</strong><br>
${employee.title}<br>
${company.name}<br>
${companyWebsiteTag}`;
    setFormData({
      ...formData,
      employeeId: employee.id,
      name: employee.name,
      signature,
    });
  };

  useEffect(() => {
    getInboxes();
    getLinkedInIntegrations();
    getSmsIntegrations();
    getTrackingDomains();
    getAgents();
  }, []);

  useEffect(() => {
    if (profile) {
      setFormData({
        id: profile.id,
        name: profile.name || "",
        email: profile.email || "",
        signature: profile.signature || "",
        inboxId: profile.inboxId || "",
        integrationId: profile.integrationId || "",
        linkedinIntegrationId: profile.linkedinIntegrationId || "",
        phoneIntegrationId: profile.phoneIntegrationId || "",
        phone: profile.phone || "",
        voiceId: profile.voiceId || "",
        agentId: profile.agentId || "",
        dailySendingLimit: profile.dailySendingLimit || 15,
        trackingDomainId: profile.trackingDomainId || "",
      });
      // Set the selected inbox when editing
      if (profile.inboxId && inboxes.length > 0) {
        const inbox = inboxes.find(
          (inbox: any) => inbox.id === profile.inboxId
        );
        setSelectedInbox(inbox || null);
      }
    } else {
      setFormData({
        name: "",
        email: "",
        signature: "",
        inboxId: "",
        integrationId: "",
        linkedinIntegrationId: "",
        phoneIntegrationId: "",
        phone: "",
        voiceId: "",
        agentId: "",
        dailySendingLimit: 15,
        trackingDomainId: "",
      });
      setSelectedInbox(null);
    }
  }, [profile, inboxes, trackingDomains]);

  // Update email when integrations load for IMAP inboxes
  useEffect(() => {
    if (
      selectedInbox &&
      selectedInbox.inboxType === "imap" &&
      selectedInbox.integrationId &&
      integrations &&
      integrations.length > 0
    ) {
      const integration = integrations.find(
        (int: any) => int.id === selectedInbox.integrationId
      );
      if (
        integration &&
        integration.parameters &&
        integration.parameters.email
      ) {
        setFormData((prev: any) => ({
          ...prev,
          email: integration.parameters.email,
          // Also update the name if it's not already set
          name: prev.name || integration.parameters.email.split("@")[0],
        }));
      }
    }
  }, [selectedInbox, integrations]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <Typography variant={"h6"}>
            {profile ? "Edit Profile" : "Create Profile"}
          </Typography>
          <IconButton onClick={() => handleClose()}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <form
        onSubmit={onSubmit}
        style={{
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          flex: 1,
          overflow: "hidden",
        }}
      >
        <DialogContent dividers>
          <DialogContentText>
            Create a sender profile to use in your sequences. First, choose the
            email integration to use for sending emails. Optionally select an
            inbox to store incoming mail. Finally, type out your personal
            signature or select an {employeeLower} to assign to this profile.
            The {employeeLower}'s name, email, and signature will be used in the
            profile. Click{" "}
            <Link
              href={`/dashboard/companies/${companyId}/inbox`}
              target="_blank"
            >
              here
            </Link>{" "}
            to configure a new inbox.
          </DialogContentText>
          <Box sx={{ mt: 2 }}>
            <SearchableIntegrationSelect
              integrations={integrations}
              selectedId={formData.integrationId}
              onChangeSingle={(id, integration) => {
                const next: any = { ...formData, integrationId: id };
                const integrationEmail = integration?.parameters?.email;
                if (integrationEmail && !formData.email) {
                  next.email = integrationEmail;
                  if (!formData.name) {
                    next.name = integrationEmail.split("@")[0];
                  }
                }
                setFormData(next);
              }}
              label="Email Integration (Send)"
              helperText="Pick any configured email sender (SMTP, SendGrid, SES, Gmail, etc.). An inbox is not required."
              onManageIntegrations={() =>
                window.open(
                  `/dashboard/companies/${companyId}/integrations`,
                  "_blank"
                )
              }
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <SearchableInboxSelect
              inboxes={inboxes}
              selectedId={formData.inboxId}
              onChangeSingle={onInboxChange}
              label="Inbox (Optional)"
              loading={inboxesLoading}
              onManageInboxes={() =>
                window.open(
                  `/dashboard/companies/${companyId}/inbox`,
                  "_blank"
                )
              }
            />
          </Box>

          <Box sx={{ mt: 3 }}>
            <EmailLimitSlider
              value={formData.dailySendingLimit}
              onChange={(value) =>
                setFormData({ ...formData, dailySendingLimit: value })
              }
              label="Daily Sending Limit"
              helperText="Maximum number of emails that can be sent from this sender profile per day. Keep this low for cold outbound from a personal mailbox to protect sender reputation. For mailboxes dedicated to mass marketing (newsletters to opted-in lists on a warmed domain / dedicated IP), it's fine to raise this well above the cold-outreach range."
              warningThreshold={50}
              warningText="⚠️ Values over 50 are intended for marketing platform sender accounts"
              min={1}
              max={1000}
              step={10}
            />
          </Box>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>LinkedIn Integration (Optional)</InputLabel>
            <Select
              value={formData.linkedinIntegrationId}
              label="LinkedIn Integration (Optional)"
              onChange={(e: any) =>
                setFormData({
                  ...formData,
                  linkedinIntegrationId: e.target.value,
                })
              }
            >
              <MenuItem value="">None</MenuItem>
              {linkedinIntegrations.map((integration: any) => (
                <MenuItem key={integration.id} value={integration.id}>
                  {integration.name || "LinkedIn Account"}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Select a LinkedIn account for this sender profile. This profile's LinkedIn account will be used first for all LinkedIn actions. If not set here, the sequence-level LinkedIn integrations will be used. This ensures the same account is used for all communications with each recipient.
            </FormHelperText>
          </FormControl>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>SMS Integration (Optional)</InputLabel>
            <Select
              value={formData.phoneIntegrationId}
              label="SMS Integration (Optional)"
              onChange={(e: any) => {
                const newPhoneIntegrationId = e.target.value;
                setFormData({
                  ...formData,
                  phoneIntegrationId: newPhoneIntegrationId,
                  phone: "", // Reset phone when integration changes
                });
              }}
            >
              <MenuItem value="">None</MenuItem>
              {smsIntegrations.map((integration: any) => (
                <MenuItem key={integration.id} value={integration.id}>
                  {integration.instanceName ||
                    integration.name ||
                    "Twilio Account"}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Select an SMS provider for this sender profile. This profile's SMS integration will be used first for all SMS actions. If not set here, the sequence-level SMS integrations will be used.
            </FormHelperText>
          </FormControl>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Phone Number (Optional)</InputLabel>
            <Select
              value={formData.phone}
              label="Phone Number (Optional)"
              onChange={(e: any) =>
                setFormData({ ...formData, phone: e.target.value })
              }
              disabled={!formData.phoneIntegrationId}
            >
              <MenuItem value="">None</MenuItem>
              {availablePhoneNumbers
                .filter(
                  (number: any) =>
                    number.integrationId === formData.phoneIntegrationId
                )
                .map((number: any) => (
                  <MenuItem key={number.phoneNumber} value={number.phoneNumber}>
                    {number.friendlyName} ({number.phoneNumber})
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <Box sx={{ mt: 2 }}>
            <VoiceSelector
              value={formData.voiceId || null}
              onChange={(voiceId) =>
                setFormData({ ...formData, voiceId: voiceId || "" })
              }
              companyId={companyId}
              getToken={getToken as () => Promise<string>}
              label="Voice (Optional)"
              helperText="Select a voice for phone calls and voice messages"
              showTestButton={true}
            />
          </Box>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Agent (Optional)</InputLabel>
            <Select
              value={formData.agentId}
              label="Agent (Optional)"
              onChange={(e: any) =>
                setFormData({ ...formData, agentId: e.target.value })
              }
            >
              <MenuItem value="">None</MenuItem>
              {agents.map((agent: any) => (
                <MenuItem key={agent.id} value={agent.id}>
                  {agent.name}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Select an agent for agent conversation steps. When this sender profile is used,
              this agent will be used for all agent conversation actions.
            </FormHelperText>
          </FormControl>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Custom Tracking Domain (Optional)</InputLabel>
            <Select
              value={formData.trackingDomainId}
              label="Custom Tracking Domain (Optional)"
              onChange={(e: any) =>
                setFormData({ ...formData, trackingDomainId: e.target.value })
              }
            >
              <MenuItem value="">Use Default</MenuItem>
              {trackingDomains.map((domain: any) => (
                <MenuItem key={domain.id} value={domain.id}>
                  {domain.subdomain}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Use a custom branded subdomain for email tracking links (e.g., track.yourbrand.com).
              Configure tracking domains using the "Tracking Domains" button above.
            </FormHelperText>
          </FormControl>

          <Button
            fullWidth
            sx={{ mt: 2 }}
            onClick={() => setEmployeeSelectOpen(true)}
            startIcon={<GroupOutlined />}
            variant={"outlined"}
            disabled={!formData.integrationId}
          >
            Select {employeeText}
          </Button>
          <TextField
            fullWidth
            sx={{ mt: 2 }}
            label="Name"
            name="name"
            value={formData.name}
            onChange={onChange}
            disabled={!formData.integrationId}
          />
          <TextField
            fullWidth
            sx={{ mt: 2 }}
            label="Email"
            name="email"
            value={formData.email}
            onChange={onChange}
            disabled={!formData.integrationId}
            helperText="Email is auto-populated from the selected inbox or integration when available. You can override it if needed."
          />
          <Box
            sx={{
              mt: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Email Signature
            </Typography>
            <EditorProvider>
              <RswEditor
                style={{ height: 300 }}
                containerProps={{ style: { resize: "vertical" } }}
                value={formData.signature}
                onChange={(e) =>
                  setFormData({ ...formData, signature: e.target.value })
                }
                disabled={!formData.integrationId}
              ></RswEditor>
            </EditorProvider>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant={"text"}>Cancel</Button>
          <LoadingButton
            disabled={!formData.integrationId}
            loading={loading}
            variant={"contained"}
            type="submit"
          >
            {profile ? "Update" : "Create"}
          </LoadingButton>
        </DialogActions>
      </form>
      <SelectEmployeeDialog
        open={employeeSelectOpen}
        onClose={() => setEmployeeSelectOpen(false)}
        title={`Select ${employeeText}`}
        content={`Select ${employeeLower} to assign to this profile`}
        buttonText="Submit"
        employees={employees}
        onSubmit={onEmployeeSelect}
      />
    </Dialog>
  );
};
